import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../../../../hooks/usedebounce';
import { ChartStatusEnum } from '../../../../models/chart';
import { ClerkListResponse } from '../../../../models/clerks';
import { ChartsService } from '../../../../services/charts-service';
import { ClerksService } from '../../../../services/clerks-service';

export type AdminClerksState = {
	loading: boolean;
	query: string;
	debouncedQuery: string;
	setQuery: (query: string) => void;
	clerks: ClerkListResponse[];
	updateShowAdminClerks: (show: boolean) => void;
	updateShowDeletedClerks: (show: boolean) => void;
	showAdmin: boolean;
	showDeleted: boolean;
	downloadCharts: (clerkId: string) => Promise<void>;
	printCharts: (clerkId: string) => Promise<void>;
	markChartsAsPrinted: (clerkId: string) => Promise<void>;
	editClerk: (clerkId: string) => void;
	deleteClerk: (clerkId: string) => void;
	promoteClerk: (clerkId: string) => void;
	removeClerkFromAdminRole: (clerkId: string) => void;
	resetPassword: (clerkId: string) => void;
}

export default function useAdminClerks(): AdminClerksState {
	const navigate = useNavigate();
	const [loading, setLoading] = useState<boolean>(true);
	const [query, setQuery] = useState<string>("");
	const [clerks, setClerks] = useState<ClerkListResponse[]>([]);
	const [showAdmin, setShowAdmin] = useState<boolean>(false);
	const [showDeleted, setShowDeleted] = useState<boolean>(false);
	const [notPrinted, setNotPrinted] = useState<boolean>(false);
	const [printRequested, setPrintRequested] = useState<boolean>(false);
	const debouncedQuery = useDebounce(query, 800);

	const getClerks = useCallback(
		async (
			query: string,
			showAdmin: boolean,
			showDeleted: boolean,
		) => {
			if (debouncedQuery) {
				setLoading(true);
			}

			try {
				setLoading(true);
				const newClerks = await ClerksService.getClerks(
					showAdmin,
					showDeleted,
					ChartStatusEnum.Completed,
					undefined,
					undefined,
					query,
					"false"
				);
				setClerks(newClerks);
			} catch (e) {
			} finally {
				setLoading(false);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[debouncedQuery, showAdmin, showDeleted],
	);

	useEffect(() => {
		getClerks(query, showAdmin, showDeleted);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedQuery, showAdmin, showDeleted]);

	const updateShowAdminClerks = (show: boolean) => {
		setShowAdmin(show);
		getClerks(query, show, showDeleted);
	}

	const updateShowDeletedClerks = (show: boolean) => {
		setShowDeleted(show);
		getClerks(query, showAdmin, show);
	}

	const downloadCharts = async (clerkId: string) => {
		const clerk = clerks.find(clerk => clerk.id === clerkId)
		const chartIds = clerk!.charts.filter(chart => chart.status === ChartStatusEnum.Completed).map(chart => chart.id);
		await toast.promise(ChartsService.downloadAll(chartIds, false), {
			loading: "Loading...",
			error: "Files download failed!",
			success: ({ data, filename }) => {
				const href = URL.createObjectURL(data);
				const link = document.createElement("a");
				link.href = href;
				link.setAttribute("download", filename);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(href);
				return "Successful download!";
			},
		});
	}

	const printCharts = async (clerkId: string) => {
		const clerk = clerks.find(clerk => clerk.id === clerkId)
		const chartIds = clerk!.charts.filter(chart => chart.status === ChartStatusEnum.Completed).map(chart => chart.id);
		await toast.promise(ChartsService.printAll(chartIds, false), {
			loading: "Loading...",
			error: "Files download failed!",
			success: ({ data, filename }) => {
				const href = URL.createObjectURL(data);
				const link = document.createElement("a");
				link.href = href;
				link.setAttribute("download", filename);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(href);
				return "Successful download!";
			},
		});
	}

	const markChartsAsPrinted = async (clerkId: string) => {
		const clerk = clerks.find(clerk => clerk.id === clerkId)
		const chartIds = clerk!.charts.filter(chart => chart.status === ChartStatusEnum.Completed && !chart.printed).map(chart => chart.id);
		await toast.promise(ChartsService.markPrintRequestedChartsAsPrinted(chartIds), {
			loading: "Loading...",
			error: "Failed to mark charts as printed!",
			success: "Successfully marked charts as printed!",
		});
	}


	const editClerk = async (clerkId: string) => {
		navigate(`/clerk/${clerkId}/edit`);
	}

	const deleteClerk = async (clerkId: string) => {
		await ClerksService.deleteClerk(clerkId);
		await getClerks(query, showAdmin, showDeleted);
	}

	const promoteClerk = async (clerkId: string) => {
		await ClerksService.makeClerkAdmin(clerkId);
	}

	const removeClerkFromAdminRole = async (clerkId: string) => {
		await ClerksService.removeClerkFromAdminRole(clerkId);
	}

	const resetPassword = async (clerkId: string) => {
		await ClerksService.resetPassword(clerkId);
	}

	return {
		loading,
		query,
		debouncedQuery,
		setQuery,
		clerks,
		showAdmin,
		showDeleted,
		updateShowAdminClerks,
		updateShowDeletedClerks,
		downloadCharts,
		printCharts,
		markChartsAsPrinted,
		editClerk,
		deleteClerk,
		promoteClerk,
		removeClerkFromAdminRole,
		resetPassword,
	};
}
